import { useEffect, useMemo, useState } from 'react'
import type { PipelineCatalogEntry } from 'app/components/action-center/usePipelinesCatalog'
import { useListCopy } from 'app/hooks/useListCopy'
import { usePracticeLists } from 'app/hooks/usePracticeLists'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useListMembershipStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import { membershipEntries, practiceListCatalog, stageIdForName } from 'lib/listMemberships'
import { logError } from 'lib/logging'
import { defaultStageIdOf, isFilterList } from 'lib/practiceLists'

/**
 * One contact's practice lists for the drawer, the inbox card and the Action Center rail: what to draw, what to
 * offer, and the writes, which report their own failures. Call from an `observer()` component.
 */
export function usePracticeListMemberships(contact: ContactJson | undefined, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true
  const store = useListMembershipStore()
  const copy = useListCopy()
  const { lists, loading: listsLoading } = usePracticeLists({ enabled })
  const contactId = contact?.id
  const [failedContactId, setFailedContactId] = useState<string>()

  useEffect(() => {
    if (!enabled || !contactId) return
    store.load([contactId]).catch((error) => {
      logError(`Reading a contact's lists failed`, error)
      setFailedContactId(contactId)
    })
  }, [enabled, contactId, store])

  const shownLists = enabled ? lists : []
  const memberships = enabled && contactId ? store.membershipsOf(contactId) : []
  const catalog = useMemo(() => practiceListCatalog(shownLists), [shownLists])
  const entries = membershipEntries(memberships, shownLists)
  const memberIds = new Set(entries.map((entry) => entry.id))
  const filterIds = new Set(shownLists.filter(isFilterList).map((list) => list.id))
  // Until the contact's own read lands, a list they are already on looks joinable, and re-adding one reports a
  // stage the write never set.
  const membershipsLoaded = !!contactId && store.isLoaded(contactId)
  const addable = membershipsLoaded
    ? catalog.filter((entry) => !memberIds.has(entry.id) && !filterIds.has(entry.id))
    : []
  const listById = new Map(shownLists.map((list) => [list.id, list]))

  const changeStage = async (listId: string, stageName: string) => {
    const list = listById.get(listId)
    const stageId = list && stageIdForName(list, stageName)
    if (!contact || !stageId) return
    try {
      await store.move(listId, contact, stageId)
    } catch (error) {
      logError(`Moving a contact on a list failed`, error)
      notifyError(copy.failedToUpdateList, `list-update-error`)
    }
  }

  const remove = async (listId: string) => {
    if (!contact || filterIds.has(listId)) return
    try {
      await store.remove(listId, contact)
    } catch (error) {
      logError(`Removing a contact from a list failed`, error)
      notifyError(copy.failedToUpdateList, `list-update-error`)
    }
  }

  const add = async (listId: string, stageName?: string) => {
    const list = listById.get(listId)
    const stageId = list && (stageName ? stageIdForName(list, stageName) : defaultStageIdOf(list))
    if (!contact || !stageId || filterIds.has(listId)) return false
    const { added } = await store.add(listId, [contact], stageId)
    if (added.length === 0) {
      notifyError(copy.errorAddingContactToList, `list-add-error`)
      return false
    }
    notifySuccess(copy.contactAddedToList)
    return true
  }

  return {
    loading: enabled && (listsLoading || (!!contactId && !membershipsLoaded && failedContactId !== contactId)),
    catalog,
    entries,
    addable,
    isPracticeListId: (id: string) => listById.has(id),
    canRemove: (listId: string) => !filterIds.has(listId),
    changeStage,
    remove,
    add,
  }
}
